import { useCallback, type RefObject } from 'react';

import { useValidationAndFormatting } from './useValidationAndFormatting.js';
import { useSelection } from '../../hooks/useSelection.js';
import type { SpinButtonTextSelection } from '../../types.js';
import type { NumberSpinButtonProps } from '../types.js';

const reNumber = new RegExp('^-?\\d*$');
const reNonNegativeNumber = new RegExp('^\\d*$');

/**
 * Returns the number of digits for a given value.
 * If the number is negative, add an additional character for the sign
 */
function getNrOfDigits(value: number) {
  return value < 0
    ? 2 + Math.floor(Math.log10(Math.abs(value)))
    : 1 + Math.floor(Math.log10(value));
}

/**
 * Processes the given value for provided input type and selection.
 */
function processInputEvent(
  value: string,
  inputType: string,
  selection: SpinButtonTextSelection,
  subsitute: string,
) {
  const collapsed = selection.start === selection.end;
  switch (inputType) {
    case 'insertText':
    case 'insertFromPaste':
      return (
        value.substring(0, selection.start) +
        subsitute +
        value.substring(selection.end)
      );
    case 'deleteByCut':
      return (
        value.substring(0, selection.start) + value.substring(selection.end)
      );
    case 'deleteContentBackward':
      return collapsed
        ? value.substring(0, selection.start - 1) +
            value.substring(selection.end)
        : value.substring(0, selection.start) + value.substring(selection.end);
    case 'deleteContentForward':
      return collapsed
        ? value.substring(0, selection.start) +
            value.substring(selection.end + 1)
        : value.substring(0, selection.start) + value.substring(selection.end);
    case 'insertCompositionText':
      return value;
    case 'deleteByDrag':
    case 'insertFromDrop':
    default:
      // Ignore drag&drop and other unknown input types for now.
      return undefined;
  }
}

/**
 * Calculates a candidate value based on the source text, the current selection and the input type.
 */
function getCandidate(
  text: string,
  domText: string,
  selection: SpinButtonTextSelection,
  inputType: string,
  substitute: string,
  formatLength: number,
) {
  // If padding is enabled, calculate the nr. of padding slots.
  const candidateOffset =
    formatLength > 0 ? Math.max(formatLength - text.length, 0) : 0;

  // Trying to forwardDelete a padding character should not change anything.
  if (
    selection.start < candidateOffset &&
    inputType === 'deleteContentForward'
  ) {
    return undefined;
  }

  // Use domtext instead of text as candidate input, if content edit happens within the padding area
  const useDomText =
    selection.start < candidateOffset &&
    (inputType === 'insertText' || inputType === 'insertFromPaste');

  // If the domtext is used, only take the part right of the cursor as value.
  // This makes modifying the text within the padding area feel more natural.
  const value = useDomText ? domText.substring(selection.end) : text;

  // Offset the selection position.
  const candidateSelection: SpinButtonTextSelection = {
    start: Math.max(selection.start - candidateOffset, 0),
    end: Math.max(selection.end - candidateOffset, 0),
  };

  // Process the input event and get the actual candidate.
  return processInputEvent(value, inputType, candidateSelection, substitute);
}

/**
 * Calculates a new cursor position for a given input event, selection and padding.
 */
function getNewSelection(
  selection: SpinButtonTextSelection,
  candidate: string,
  substitute: string,
  inputType: string,
  formatLength: number,
) {
  // Right align mode is only active, if padding is enabled and if the candidate still fits within the padded length.
  const rightAlign = formatLength > 0 && candidate.length <= formatLength;
  const collapsed = selection.start === selection.end;

  let curserPos = 0;
  switch (inputType) {
    case 'insertText':
    case 'insertFromPaste':
      // For right aligned mode, keep the cursor position at the end of the selection.
      // For left aligned mode, set the cursor to the end of the inserted text.
      curserPos = rightAlign
        ? selection.end
        : selection.start + substitute.length;
      break;
    case 'deleteContentBackward':
      // For right aligned mode, keep the cursor postion at the end of the selection.
      // For left aligned mode and if the selection is not collapsed, keep the cursor position. Otherwise move the cursor left.
      curserPos = rightAlign
        ? selection.end
        : collapsed
        ? selection.start - 1
        : selection.start;
      break;
    case 'deleteContentForward':
      // For right aligned mode, if the selection is not collapsed, keep the cursor position. Otherwise move the cursor right.
      // For left aligned mode, keep the cursor postion at the start of the selection.
      curserPos = rightAlign
        ? collapsed
          ? selection.start + 1
          : selection.end
        : selection.start;
      break;
    case 'deleteByCut':
      // Based on the writing mode, keep the cursor at either the left or right bound of the current seletion
      curserPos = rightAlign ? selection.end : selection.start;
  }

  return {
    start: curserPos,
    end: curserPos,
  } as SpinButtonTextSelection;
}

/**
 * Hook to provide input validation using the native onBeforeInput method.
 */
export const useBeforeInputHandler = (
  ref: RefObject<HTMLElement | null> | undefined,
  props: NumberSpinButtonProps,
  applyText: (value: string, fireOnChange?: boolean) => void,
  text: string,
) => {
  const {
    onBeforeInput,
    displayFormat: { length: formatLength = 0 } = {},
    readOnly,
    disabled,
    onChange,
    minValue = Number.MIN_SAFE_INTEGER,
    maxValue = Number.MAX_SAFE_INTEGER,
  } = props;

  const { getSelection, setSelection } = useSelection(ref);
  const { parse, format } = useValidationAndFormatting(
    props.displayFormat,
    props.required,
    minValue,
    maxValue,
  );

  /** This callback validates generic number min/max and length constraints. */
  const defaultOnBeforeInput = useCallback(
    (candidate: string, event: InputEvent) => {
      // Reject candidate if it's not a valid integer.
      // Only allow leading -, if negative values are allowed.
      const isInteger =
        minValue < 0
          ? reNumber.test(candidate)
          : reNonNegativeNumber.test(candidate);

      if (!isInteger) {
        event.preventDefault();
        return;
      }

      const value = parse(candidate);
      if (value === null) {
        //  nothing else to check
        return;
      }

      // Calculate max nr. of digits for a given min or max value.
      const maxLength =
        value < 0 ? getNrOfDigits(minValue) : getNrOfDigits(maxValue);

      // Ignore candidates longer than this limit.
      if (candidate.length > maxLength) {
        event.preventDefault();
        return;
      }

      // Don't allow to enter a value > maxValue for a positive value,
      // and vice versa for negative ones.
      if (value < 0 ? value < minValue : value > maxValue) {
        event.preventDefault();
        return;
      }
    },
    [maxValue, minValue, parse],
  );

  /**
   * This callback validates the input against optional external and internal constraints and initializes the expected selection.
   */
  const beforeInputHandler = useCallback(
    (event: InputEvent) => {
      if (disabled || readOnly) {
        event.preventDefault();
        return;
      }
      const selection = getSelection();

      // Nothing to change if selection/cursor is not within the current spinbutton.
      if (!selection) {
        event.preventDefault();
        return;
      }

      const domText = ref?.current?.textContent ?? '';

      // If data is comming from the clipboard, strip formatting and use plain text version as substitute.
      const substitute =
        event.data ?? event.dataTransfer?.getData('text/plain') ?? '';

      // Calculate a candidate for given input event and configuration.
      const candidate = getCandidate(
        text,
        domText,
        selection,
        event.inputType,
        substitute,
        formatLength,
      );

      // If no candidate could be created, ignore the event.
      if (candidate === undefined) {
        event.preventDefault();
        return;
      }

      // Validate against an optional external candidate validation.
      // Break early if validation fails.
      onBeforeInput?.(candidate, event);
      if (event.defaultPrevented) {
        return;
      }

      // Validate using the internal constraints.
      // Break early if validation fails.
      defaultOnBeforeInput(candidate, event);
      if (event.defaultPrevented) {
        return;
      }

      // Calculate the new cursor position.
      const newSelection =
        domText === '' || domText === '-'
          ? { start: 0, end: 0, rtl: true } // Use last possible cursor position. (Necessary if padding is enabled)
          : getNewSelection(
              selection,
              candidate,
              substitute,
              event.inputType,
              formatLength,
            );

      // Set the internal state and dom text, then update the cursor position.
      applyText(candidate);
      setSelection(newSelection);

      // Workaround to prevent/revert dead key inputs
      if (event.inputType === 'insertCompositionText') {
        setTimeout(() => {
          if (ref?.current) {
            // Update original text content and selection
            ref.current.textContent = format(text);
            setTimeout(() => setSelection(selection), 0);
          }
        }, 0);
      }

      // Invoke onChange callback if available
      const newValue = parse(candidate);
      props.value !== newValue && onChange?.(newValue);
      event.preventDefault();
    },
    [
      disabled,
      readOnly,
      getSelection,
      ref,
      text,
      formatLength,
      onBeforeInput,
      defaultOnBeforeInput,
      applyText,
      setSelection,
      parse,
      props.value,
      onChange,
      format,
    ],
  );

  return beforeInputHandler;
};
